import { useState, useEffect, useCallback } from "react";
import { ShieldAlert, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { clsx } from "../lib/format";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

export function BotLiveToggle({ bot, onChanged, disabled, masterOff }) {
  const toggle = async (v) => {
    if (!v && !window.confirm(`Disable live trading for ${bot.name}? Any bots running live on it will stop.`)) return;
    try { await api.patch(`/admin/bots/${bot.id}/live`, { enabled: v }); toast.success(`Live trading ${v ? "enabled" : "disabled"} for ${bot.name}`); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <label className={clsx("mt-3 flex items-center justify-between rounded-lg border px-3 py-2 text-xs", bot.live_available ? "border-neg/30 bg-neg/10" : "border-white/10 bg-fx-base")} data-testid={`admin-bot-live-${bot.id}`}>
      <span className="flex items-center gap-1.5 font-semibold"><Zap className={clsx("h-3.5 w-3.5", bot.live_available ? "text-neg" : "text-fx-s3")} /> Live trading {bot.live_available ? "enabled" : "off"}{bot.live_available && masterOff && <span className="text-gold font-normal">(master OFF)</span>}</span>
      <Switch checked={!!bot.live_available} disabled={disabled} onCheckedChange={toggle} data-testid={`admin-bot-live-switch-${bot.id}`} />
    </label>
  );
}

export default function BotLiveSettings({ onChanged, isSuper, onMaster }) {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/admin/bots/live-settings").then((r) => setS(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (s) onMaster?.(s.global_enabled); }, [s, onMaster]);
  if (!s) return null;
  const run = async (fn, msg) => { try { const r = await fn(); setS(r.data); toast.success(msg); onChanged(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } };
  const setGlobal = (v) => {
    if (!v && !window.confirm("Turn off live bot trading for the whole platform? All bots running on live wallets will stop.")) return;
    run(() => api.put("/admin/bots/live-settings", { enabled: v }), `Live bot trading ${v ? "enabled" : "disabled"} platform-wide`);
  };
  const bulk = (v) => {
    if (!window.confirm(`${v ? "Enable" : "Disable"} live trading on ALL ${s.bots_total} bots?`)) return;
    run(() => api.post("/admin/bots/live-bulk", { enabled: v }), `Live trading ${v ? "enabled" : "disabled"} on all bots`);
  };
  return (
    <div className="rounded-xl bg-fx-s1 border border-white/5 p-5" data-testid="bot-live-settings">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-neg/15 grid place-items-center"><ShieldAlert className="h-5 w-5 text-neg" /></div>
          <div>
            <h2 className="font-display font-bold">Live Bot Trading</h2>
            <p className="text-xs text-fx-s3 max-w-xl">Lets KYC-verified users run bots on their live wallet with real funds. A bot can trade live only when the master switch is ON <b>and</b> live trading is enabled on that bot.</p>
            <div className="flex gap-3 mt-2 text-[11px] font-mono text-fx-s3">
              <span data-testid="bot-live-count">{s.bots_enabled}/{s.bots_total} bots live-enabled</span>
              <span className={s.live_running ? "text-neg" : ""} data-testid="bot-live-running">{s.live_running} running live now</span>
            </div>
          </div>
        </div>
        <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-fx-base px-4 py-2.5">
          <span className="text-sm font-semibold">Master switch <span className={clsx("ml-1 text-xs", s.global_enabled ? "text-pos" : "text-neg")} data-testid="bot-live-global-status">{s.global_enabled ? "ON" : "OFF"}</span></span>
          <Switch checked={s.global_enabled} disabled={!isSuper} onCheckedChange={setGlobal} data-testid="bot-live-global-switch" />
        </label>
      </div>
      {isSuper && (
        <div className="flex flex-wrap gap-2 mt-4">
          <Button size="sm" onClick={() => bulk(true)} data-testid="bot-live-enable-all" className="h-8 bg-neg hover:bg-red-600 gap-1"><Zap className="h-3.5 w-3.5" /> Enable live on all bots</Button>
          <Button size="sm" variant="outline" onClick={() => bulk(false)} data-testid="bot-live-disable-all" className="h-8 border-white/10 bg-fx-base">Disable live on all bots</Button>
        </div>
      )}
      {!s.global_enabled && <p className="mt-3 text-xs text-gold" data-testid="bot-live-global-off-note">The master switch is OFF, so no user can run a bot live right now, whatever the per-bot settings.</p>}
      {!isSuper && <p className="mt-3 text-[11px] text-fx-s3">Only the super admin can change live trading settings.</p>}
    </div>
  );
}
